import { getSupabaseClient } from './supabase.client.js';

/**
 * Repositorio de empleados (`empleados`) para el panel de staff (Control de
 * Horarios y Liquidación de Sueldos, slice 1). Habla con Supabase SOLO a
 * través del cliente anon de navegador (supabase.client.js); el acceso lo
 * enforcea RLS (`empleados_admin_all`, is_admin()). Este es el ÚNICO módulo
 * que emite queries Supabase para empleados.
 *
 * `pin_hash` NUNCA se selecciona: vive en `empleado_pines`, una tabla sin
 * policies ni grants (AD1 en design.md) — este repositorio ni siquiera
 * intenta leerla. El único camino de escritura para el PIN es la RPC
 * `set_empleado_pin` (SECURITY DEFINER), nunca un insert/update directo.
 *
 * Columnas SIEMPRE explícitas (nunca `select('*')`): así un futuro `alter
 * table` que agregue una columna sensible no se filtra por accidente acá.
 */

const EMPLEADO_COLUMNS =
    'id, nombre, foto_url, tipo_pago, valor, paga_feriado_doble, fudo_cliente_id, fudo_cliente_nombre, fudo_proveedor_id, fudo_proveedor_nombre, activo, created_at, updated_at';

/**
 * Re-lanza un error de Supabase preservando el código de Postgres (mismo
 * patrón que mesas.repository.js).
 * @param {{ message: string, code?: string }} error
 * @returns {never}
 */
function rethrow(error) {
    const err = new Error(error.message);
    if (error.code) err.code = error.code;
    throw err;
}

/**
 * Todos los empleados (activos e inactivos — la vista de administración
 * necesita ver a ambos; el kiosco usa su propia RPC de proyección fija,
 * `kiosk_presencia`, que no pasa por acá), ordenados por nombre.
 *
 * Si Supabase no está configurado devuelve `[]` (misma degradación que
 * `listBlocks` en blocks.repository.js).
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error
 */
export async function listEmpleados() {
    const client = getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
        .from('empleados')
        .select(EMPLEADO_COLUMNS)
        .order('nombre', { ascending: true });

    if (error) rethrow(error);
    return data ?? [];
}

/**
 * Crea un empleado nuevo. El PIN NO viaja acá: se setea aparte con
 * `setEmpleadoPin` (que llama a la RPC `set_empleado_pin`), después de
 * crear el registro.
 * @param {{ nombre: string, foto_url?: string|null, tipo_pago: 'hora'|'dia', valor: number, paga_feriado_doble?: boolean, fudo_cliente_id?: string|null, fudo_cliente_nombre?: string|null, fudo_proveedor_id?: string|null, fudo_proveedor_nombre?: string|null }} fields
 * @returns {Promise<object>} la fila creada
 * @throws {Error} si Supabase no está configurado o la query falla, con
 *   `error.code === '23505'` (unique_violation) si `fudo_cliente_id` o
 *   `fudo_proveedor_id` ya están asignados a otro empleado — activo o no
 *   (migración 21, AD13). El caller distingue el índice por su nombre en
 *   `error.message` (`empleados_fudo_cliente_unico` / `empleados_fudo_proveedor_unico`).
 */
export async function createEmpleado(fields) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Creación no disponible');

    const { data, error } = await client
        .from('empleados')
        .insert(fields)
        .select(EMPLEADO_COLUMNS)
        .single();

    if (error) rethrow(error);
    return data;
}

/**
 * Edita los campos de un empleado. `id` se excluye a propósito aunque venga
 * en `fields` (mismo criterio defensivo que `updateReservation`).
 * @param {string} id
 * @param {object} fields campos a actualizar (parcial)
 * @returns {Promise<object>} la fila actualizada
 * @throws {Error} si Supabase no está configurado o la query falla
 */
export async function updateEmpleado(id, fields) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Edición no disponible');

    const { id: _ignoredId, ...editables } = fields;

    const { data, error } = await client
        .from('empleados')
        .update(editables)
        .eq('id', id)
        .select(EMPLEADO_COLUMNS)
        .single();

    if (error) rethrow(error);
    return data;
}

/**
 * Setea (crea o reemplaza) el PIN de un empleado vía la RPC
 * `set_empleado_pin` (SECURITY DEFINER, admin-only, hashea con bcrypt en
 * Postgres — AD2 en design.md). El hash NUNCA sale de la base: esta función
 * solo reenvía el resultado `{ ok, error? }` que la RPC ya devuelve, jamás
 * el hash.
 * @param {{ empleadoId: string, pin: string }} params
 * @returns {Promise<{ ok: boolean, error?: string }>}
 * @throws {Error} si Supabase no está configurado o la llamada RPC falla a
 *   nivel de transporte (permiso denegado, red, etc. — distinto de un
 *   rechazo de negocio como `pin_duplicado`, que llega en `error`)
 */
export async function setEmpleadoPin({ empleadoId, pin }) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Actualización de PIN no disponible');

    const { data, error } = await client.rpc('set_empleado_pin', {
        p_empleado_id: empleadoId,
        p_pin: pin,
    });

    if (error) rethrow(error);
    return data;
}
